import React, {useEffect, useRef, useState} from 'react';
import {Helmet} from "react-helmet";
import BtnIcons from "../../../../api/images/btnIcons";
import {CondDateTimePicker} from "../../../../components";
import moment from "moment/moment";
import {useLocation} from "react-router-dom";
import {getRankTargetTypeName, getRankTypeName} from "../../../../constants/rank";
import {rankSystemUpdateRequest} from "../../../../redux/actions/app/inAppRankSystemActions";
import {useDispatch} from "react-redux";
import Notification from "../../../../components/Notification/Notification";
import {Box, Modal} from "@mui/material";

const InAppRankSystemUpdate = () => {

    const title = '인앱 랭킹 정보 수정'

    const dispatch = useDispatch();
    const location = useLocation();
    const rankData = location?.row;

    // 초기화를 위한 초기값(맨 처음에 받아오는 값)
    const initRankData = useRef();

    useEffect(() => {
        initRankData.current = rankData;
    }, [rankData])

    // 날짜
    const [selectedStartDate, setSelectedStartDate] = useState(moment.utc().format('YYYY-MM-DDTHH:mm:ss'));
    const [selectedEndDate, setSelectedEndDate] = useState(moment.utc().format('YYYY-MM-DDTHH:mm:ss'));

    // 각 항목
    const [rankType, setRankType] = useState(null);
    const [settingNo, setSettingNo] = useState(null);
    const [lastRank, setLastRank] = useState(null);
    const [targetType, setTargetType] = useState(null);
    const [withoutTarget, setWithoutTarget] = useState("");
    const [rankDetail, setRankDetail] = useState({});
    const [modifiedRankDetail, setModifiedRankDetail] = useState(""); // textarea 출력/수정용
    const [rankRewards, setRankRewards] = useState({});
    const [countingTarget, setCountingTarget] = useState("");

    // 로딩, 토스트
    const [isLoading, setIsLoading] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [showNotification, setShowNotification] = useState(false);

    // 확인 모달
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [modalConfirmHandler, setModalConfirmHandler] = useState(() => {});

    // JSON 수정 모달
    const [openJSONModal, setOpenJSONModal] = useState(false);

    function setRankData(rankData) {
        if(!rankData){
            return;
        }

        if(rankData.rankDetail){
            setRankDetail(JSON.parse(rankData.rankDetail));
        }

        setRankType(rankData.rankType);
        setSettingNo(rankData.settingNo);
        setLastRank(rankData.lastRank);
        setTargetType(rankData.targetType);
        setWithoutTarget(rankData.withoutTarget);
    }

    function handleStartDateChange(date) {
        setSelectedStartDate(moment(date).format('YYYY-MM-DDTHH:mm:ss'));
    }

    function handleEndDateChange(date) {
        setSelectedEndDate(moment(date).format('YYYY-MM-DDTHH:mm:ss'));
    }

    // rewards 배열의 특정 항목(field)을 수정하는 함수
    function handleRewardChange (rankKey, rewardIndex, field, newValue) {
        setRankRewards(prev => ({
            ...prev,
            [rankKey]: {
                ...prev[rankKey],
                rewards: prev[rankKey].rewards.map((reward, idx) =>
                    idx === rewardIndex ? { ...reward, [field]: Number(newValue) } : reward
                )
            }
        }));
    }

    // 특정 순위(rank) 보상(rewards) 배열에 새 항목을 추가하는 함수(추가 버튼 클릭)
    function handleAddReward (rankKey) {
        const newReward = { itemId: 0, value: 0 };
        setRankRewards(prev => ({
            ...prev,
            [rankKey]: {
                ...prev[rankKey],
                rewards: [...prev[rankKey].rewards, newReward]
            }
        }));
    }

    // 특정 보상 삭제 함수
    function handleDeleteReward (rankKey, rewardIndex) {
        setRankRewards(prev => ({
            ...prev,
            [rankKey]: {
                ...prev[rankKey],
                rewards: prev[rankKey].rewards.filter((_, idx) => idx !== rewardIndex)
            }
        }));
    }

    function handleRankDetailChange(key, value) {
        setRankDetail(prev => ({
            ...prev,
            [key]: value,
        }));
    }

    function handleSubmit () {
        // setRankDetail이 안 먹음; 왜지?
        const updatedRankDetail = {
            searchPeriod : {
                startTime: moment(selectedStartDate).format('YYYY-MM-DDTHH:mm:ss'),
                endTime: moment(selectedEndDate).format('YYYY-MM-DDTHH:mm:ss'),
            },
            rankRewards: rankRewards,
            countingTarget: countingTarget,
        };

        console.log("요청 rankDetail:")
        console.log(updatedRankDetail);

        setIsLoading(true);

        dispatch(
            rankSystemUpdateRequest({
                settingNo: settingNo,
                rankType: rankType,
                lastRank: lastRank,
                targetType: targetType,
                withoutTarget: withoutTarget,
                rankDetail: JSON.stringify(updatedRankDetail),
                callback: (data) => {
                    console.log('요청 결과: ', data);
                    setNotificationMessage(`${rankType} (${getRankTypeName(rankType)}) 정보를 수정하였습니다.`);
                    setShowNotification(true);
                    setIsLoading(false);

                    setOpenConfirmModal(false);
                },
                error: (e) => {
                    console.log(e);
                    setNotificationMessage(e.toString());
                    setNotificationMessage(`${rankType} (${getRankTypeName(rankType)}) 수정 요청에 실패했습니다.`);
                    setShowNotification(true);
                    setIsLoading(false);

                    setOpenConfirmModal(false);
                },
            }),
        );

    }

    // 초기화
    function handleInit() {
        setRankData(initRankData.current);
        setNotificationMessage("초기화되었습니다.");
        setShowNotification(true);
        setOpenConfirmModal(false);
    }

    function handleInitBtnClick() {
        setModalConfirmHandler(() => () => handleInit());
        setModalTitle("초기화");
        setModalMessage( `입력값이 모두 초기화됩니다. 진행하시겠습니까? `);
        setOpenConfirmModal(true);
    }

    function handleUpdateBtnClick () {
        setModalConfirmHandler(() => () => handleSubmit());
        setModalTitle("수정")
        setModalMessage( `${rankType} (${getRankTypeName(rankType)}) 수정하시겠습니까?`);
        setOpenConfirmModal(true);
    }

    // "JSON" 버튼 누르면
    function handleJSONBtnClick () {

        setModifiedRankDetail(JSON.stringify(rankDetail, null, 4));

        setOpenJSONModal(true);
    }

    // JSON 수정 모달에서 "수정" 버튼 누르면
    function handleJSONUpdateBtnClick () {
        try{
            const parsed = JSON.parse(modifiedRankDetail);
            setRankDetail(parsed);
            setOpenJSONModal(false);
            setNotificationMessage(`적용되었습니다. "수정" 버튼을 눌러야 DB에 반영됩니다.`);

            setModifiedRankDetail(JSON.stringify(rankDetail, null, 4));

            setShowNotification(true);
        }catch (e) {
            console.error(e);
            setNotificationMessage("JSON 데이터가 잘못 입력되었습니다.");
            setShowNotification(true);
        }

    }

    // 수정 시 받아오는 데이터
    useEffect(() => {
        setRankData(rankData);
    }, [rankData]);

    // 집계 순위만큼 reward 배열 초기화
    useEffect(() => {
        if(!lastRank) {
            return;
        }

        setRankRewards(prev => {
            const newRankRewards = {};
            // 1부터 lastRank 까지 반복하면서
            for (let i = 1; i <= lastRank; i++) {
                // 만약 이전 state 에 값이 있으면 그대로 사용, 없으면 rankDetail 에서 가져오거나 기본값 사용
                newRankRewards[i] =
                    prev[i] ||
                    (rankDetail && rankDetail.rankRewards && rankDetail.rankRewards[i]) ||
                    { rewards: [{ itemId: 0, value: 0 }] };
            }
            return newRankRewards;
        });
    }, [lastRank]);


    // 랭크 디테일
    useEffect(() => {
        if(!rankDetail){
            return;
        }

        console.log("rankDetail");
        console.log(rankDetail);
        console.log(JSON.stringify(rankDetail));

        setSelectedStartDate(moment(rankDetail?.searchPeriod?.startTime).format('YYYY-MM-DDTHH:mm:ss'));
        setSelectedEndDate(moment(rankDetail?.searchPeriod?.endTime).format('YYYY-MM-DDTHH:mm:ss'));
        setCountingTarget(rankDetail?.countingTarget)

        if(rankDetail.rankRewards){
            setRankRewards(JSON.parse(JSON.stringify(rankDetail?.rankRewards)));
        }

    }, [rankDetail, rankData])


    return (
        <div className={'page'}>
            <Helmet>
                <title>{title}</title>
            </Helmet>

            <div className="topSectionBack" style={{width:'100%', display:'flex', justifyContent:"space-between"}}>

                <div className="leftDiv" style={{display:"flex", alignItems:"center", gap: 5}}>
                    <img
                        src={BtnIcons.backIcon}
                        className="backIcon"
                        onClick={() => {
                            window.history.back();
                        }}
                        alt="Back"
                    />
                    <p className="titleText">{title}</p>

                </div>

                <div className="rightDiv">
                    <div className="rightBtn">
                        <button
                            className="resetBtn"
                            onClick={handleInitBtnClick}
                        >
                            초기화
                        </button>
                        <button
                            className="defaultBtn"
                            onClick={handleUpdateBtnClick}
                        >
                            수정
                        </button>
                    </div>
                </div>
            </div>
            {/* 뒤로가기, 제목 */}

            <div className="spaceDiv10" />

            <div className="contentsArea">
                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">랭크 타입</div>
                    <div className="rightDiv col">
                        <input disabled value={rankType} />
                    </div>
                </div>

                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">이름</div>
                    <div className="rightDiv col">
                        <input
                            disabled
                            className="eventName"
                            value={getRankTypeName(rankType)}
                        />
                    </div>
                </div>

                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">대상 타입</div>
                    <div className="rightDiv col">
                        <input
                            disabled
                            value={getRankTargetTypeName(targetType)} />
                    </div>
                </div>

                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">집계 순위<p className="reqVal">*</p></div>
                    <div className="rightDiv">
                        <input
                            type={'number'}
                            value={lastRank}
                            style={{ width: 'fit-content' }}
                            onChange={(e) => {
                                setLastRank(Number(e.target.value));
                            }}
                        />
                        위까지 집계
                    </div>
                </div>

                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">제외 대상</div>
                    <div className="rightDiv">
                        <input
                            placeholder={':로 구분 ex) 100:110:150'}
                            type="text"
                            value={withoutTarget}
                            onChange={(e) => {
                                setWithoutTarget(e.target.value);
                            }}
                        />
                    </div>
                </div>

                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">
                        기간<p className="reqVal">*</p>
                    </div>
                    <div className="rightDiv date">
                        <CondDateTimePicker
                            disable={false}
                            value={selectedStartDate}
                            onChange={(date) => {
                                handleStartDateChange(
                                    moment(date).format('YYYY-MM-DDTHH:mm:ss'),
                                );
                            }}
                        />
                        부터
                        <CondDateTimePicker
                            disable={false}
                            value={selectedEndDate}
                            onChange={(date) => {
                                handleEndDateChange(
                                    moment(date).format('YYYY-MM-DDTHH:mm:ss'),
                                );
                            }}
                        />
                        까지
                    </div>
                </div>
                {/* rowDiv 기간 설정 */}

                <div className="spaceDiv10" />

                <div className="rowDiv">
                    <div className="leftDiv">집계 기준</div>
                    <div className="rightDiv">
                        <input
                            placeholder={''}
                            type="text"
                            value={countingTarget}
                            onChange={(e) => {
                                handleRankDetailChange('countingTarget', e.target.value.trim());
                            }}
                        />
                    </div>
                </div>

                <div className="spaceDiv10" />
                <div className="spaceDiv10" />


                <div className="rowDiv">
                    <div className="leftDiv"></div>
                    <div
                        className="rightDiv"

                        style={{display: 'flex',
                            justifyContent: 'flex-end'}}

                    >
                        <button
                            className={"defaultBtn"}
                            onClick={handleJSONBtnClick}
                        >
                            JSON으로 입력
                        </button>
                    </div>
                </div>

                <div className="spaceDiv10" />
                <div className="spaceDiv10" />

                {/**
                 * rankRewards
                 *
                 * {
                 *     "searchPeriod": {
                 *         "startTime": "2025-02-01T00:00:00",
                 *         "endTime": "2025-02-28T23:59:59"
                 *     },
                 *     "rankRewards": {
                 *         "1": {
                 *             "rewards": [
                 *                 {
                 *                     "itemId": 0,
                 *                     "value": 100
                 *                 }
                 *             ]
                 *         },
                 *         "2": {
                 *             "rewards": [
                 *                 {
                 *                     "itemId": 0,
                 *                     "value": 50
                 *                 }
                 *             ]
                 *         },
                 *      ...
                 *     }
                 * }
                 */}
                {Object.entries(rankRewards).map(([key, value]) => {
                    return (
                        <div
                            key={`rankReward-${key}`}
                        >
                            <div className="sectionLine" />
                            <div className="spaceDiv10" />
                            <div className="spaceDiv10" />

                            <div className="rowDiv">
                                <div className="leftDiv">
                                    <p>{key}</p>
                                </div>
                                <div
                                    className="rightDiv"
                                    style={{ display: 'flex', flexWrap: 'wrap' }}
                                >
                                    {value?.rewards?.map((item, index) => {
                                        return (
                                            <>
                                                <div
                                                    key={`itemContainer-${index}`}
                                                    className="itemContainer"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 20,
                                                        flexWrap: 'wrap',
                                                        width: '90%',
                                                    }}
                                                >
                                                    <div className="item">
                                                        <label about={'itemId'}>아이템 ID </label>
                                                        <input
                                                            className={'itemId'}
                                                            type={'text'}
                                                            value={item.itemId}
                                                            onChange={(e) =>
                                                                handleRewardChange(
                                                                    key,
                                                                    index,
                                                                    'itemId',
                                                                    Number(e.target.value),
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    <div className="item">
                                                        <label about={'itemValue'}>수량 </label>
                                                        <input
                                                            className={'itemValue'}
                                                            type={'number'}
                                                            value={item.value}
                                                            onChange={(e) =>
                                                                handleRewardChange(
                                                                    key,
                                                                    index,
                                                                    'value',
                                                                    Number(e.target.value),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <button
                                                        className={'error'}
                                                        style={{
                                                            width: 'fit-content',
                                                            paddingInline: 20,
                                                        }}
                                                        onClick={() => handleDeleteReward(key, index)}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>

                                                <div className="spaceDiv10" />
                                            </>
                                        );
                                    })}
                                    <button
                                        className={'resetBtn'}
                                        style={{ width: 'fit-content', paddingInline: 20 }}
                                        onClick={() => handleAddReward(key)}
                                    >
                                        추가
                                    </button>
                                </div>
                            </div>
                            <div className="spaceDiv10" />
                            <div className="spaceDiv10" />
                        </div>
                    );
                })}
                {/* 순위별 rewards */}

            </div>
            {/* contentsArea */}

            <div className="spaceDiv10" />

            <div className="rightBtn">
                <button
                    className="resetBtn"
                    onClick={handleInitBtnClick}
                >
                    초기화
                </button>
                <button
                    className="defaultBtn"
                    onClick={handleUpdateBtnClick}
                >
                    수정
                </button>
            </div>

            {/* 확인 모달 */}
            <Modal
                open={openConfirmModal}
                onClose={() => setOpenConfirmModal(false)}
                aria-labelledby="confirm-modal-title"
                aria-describedby="confirm-modal-description"
            >
                <Box className="modalBox">
                    <div className="modalTitle">
                        {modalTitle}
                        <img
                            src={BtnIcons.closeIcon}
                            onClick={() => setOpenConfirmModal(false)}
                            style={{ cursor: 'pointer', float: 'right' }}
                            alt="Close"
                        />
                    </div>
                    <div className="sectionLine" />
                    <div className="descText">{modalMessage}</div>
                    <div className="rightBtn padding">
                        <button
                            className="cancelBtn purple"
                            onClick={() => {
                                setNotificationMessage('취소되었습니다.');
                                setShowNotification(true);
                                setOpenConfirmModal(false);
                            }}
                        >
                            취소
                        </button>
                        <button
                            className="endBtn error"
                            onClick={modalConfirmHandler}
                        >
                            확인
                        </button>
                    </div>
                </Box>
            </Modal>

            {/* JSON으로 수정 모달 */}
            <Modal
                open={openJSONModal}
                onClose={() => setOpenJSONModal(false)}

            >
                <Box
                    className="modalBox paddingAll"
                    style={{
                        display : "flex",
                        flexDirection : "column",
                        flexWrap: "wrap",
                        width : "fit-content",
                        overflowY: "auto"
                    }}
                >
                    <div className="modalHeader" style={{display : "flex", justifyContent : "space-between"}}>
                        <div className="modalTitle">JSON으로 수정</div>
                        <img src={BtnIcons.closeIcon} alt="" onClick={() => {setOpenJSONModal(false)}}/>
                    </div>
                    <div className="spaceDiv10" />
                    <textarea
                        cols={50}
                        rows="40"
                        value={modifiedRankDetail}
                        onChange={(e) => {
                            setModifiedRankDetail(e.target.value);
                        }}
                    />
                    <div className="spaceDiv10" />
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 5
                    }}
                    >
                        <button
                            className={"defaultBtn error"}
                            onClick={() => {setModifiedRankDetail(JSON.stringify(rankDetail, null, 4))}}

                        >
                            초기화
                        </button>
                        <button
                            className={"defaultBtn"}
                            onClick={handleJSONUpdateBtnClick}
                        >
                            적용
                        </button>
                    </div>


                </Box>
            </Modal>

            {/* 토스트 */}
            {showNotification ? (
                <Notification
                    close={() => setShowNotification(false)}
                    message={notificationMessage}
                />
            ) : null}

            <div className="spaceDiv10" />
            <div className="spaceDiv10" />
            <div className="spaceDiv10" />
            <div className="spaceDiv10" />
            <div className="spaceDiv10" />
        </div>
    );
};

export default InAppRankSystemUpdate;


